import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search,
    Car,
    User,
    FileText,
    History,
    Command,
    ArrowRight,
    SearchX,
    type LucideIcon,
} from 'lucide-react';
import { IconTile } from '@/components/ui/icon-tile';
import { EmptyState } from '@/components/ui/empty-state';
import { Kbd } from '@/components/ui/kbd';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { vehicleService } from '@/features/vehicles/services/vehicle.service';
import { Loader2 } from 'lucide-react';

interface SearchResult {
    id: string;
    type: 'vin' | 'license' | 'case' | 'user';
    title: string;
    subtitle: string;
    href: string;
}

export const SearchModal = ({ open, setOpen }: { open: boolean, setOpen: (open: boolean) => void }) => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const handler = setTimeout(async () => {
            if (!query) {
                setResults([]);
                return;
            }

            setIsLoading(true);
            try {
                // Base mock results
                const baseResults: SearchResult[] = ([
                    { id: '1', type: 'vin', title: 'JN1-AZ456-B', subtitle: '2010 Honda Ridgeline - Salvage Intel', href: '/vin-lookup' },
                    { id: '2', type: 'case', title: 'CASE-2024-001', subtitle: 'Under Investigation - EOCO Fraud Registry', href: '/eoco/cases' },
                    { id: '3', type: 'license', title: 'GHA-7788-2', subtitle: 'Driver License: Kwesi Arthur - Active', href: '/vehicle-history' },
                    { id: '4', type: 'vin', title: 'WBA-XYZ-991', subtitle: '2022 BMW M4 - Clean Title Check', href: '/vin-lookup' },
                ] as const).filter(r =>
                    r.title.toLowerCase().includes(query.toLowerCase()) ||
                    r.subtitle.toLowerCase().includes(query.toLowerCase())
                ) as SearchResult[];

                // Live API call if query looks like a potential VIN (min 10 chars usually, but let's allow 5+ for starts)
                if (query.length >= 5) {
                    try {
                        const response = await vehicleService.getVehicleHistory(query.toUpperCase());
                        if (response.data) {
                            const liveResult: SearchResult = {
                                id: `live-${response.data.vin}`,
                                type: 'vin',
                                title: response.data.vin,
                                subtitle: `${response.data.year} ${response.data.make} - Live Registry Record`,
                                href: '/vin-lookup'
                            };
                            // Add only if not already in mock (though mock id is different)
                            setResults([liveResult, ...baseResults.filter(r => r.title !== response.data.vin)]);
                        } else {
                            setResults(baseResults);
                        }
                    } catch (err) {
                        // Silent err, just use mocks
                        setResults(baseResults);
                    }
                } else {
                    setResults(baseResults);
                }
            } finally {
                setIsLoading(false);
            }
        }, 300);

        return () => clearTimeout(handler);
    }, [query]);

    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen(!open);
            }
        };

        document.addEventListener('keydown', down);
        return () => document.removeEventListener('keydown', down);
    }, [open, setOpen]);

    const handleSelect = (result: SearchResult) => {
        setOpen(false);
        setQuery('');
        if (result.type === 'vin') {
            navigate(result.href, { state: { vin: result.title } });
        } else {
            navigate(result.href);
        }
    };

    // Reset highlighted row whenever the result list changes
    useEffect(() => { setActiveIndex(0); }, [results]);

    const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!results.length) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => (i + 1) % results.length); }
        if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => (i - 1 + results.length) % results.length); }
        if (e.key === 'Enter') { e.preventDefault(); handleSelect(results[activeIndex]); }
    };

    const quickLinks = [
        { label: 'VIN lookup', icon: Car, onClick: () => { setOpen(false); navigate('/vin-lookup'); } },
        { label: 'Stolen vehicles', icon: FileText, onClick: () => { setOpen(false); navigate('/police/stolen'); } },
        { label: 'Vehicle registry', icon: History, onClick: () => { setOpen(false); navigate('/vehicle-history'); } },
        { label: 'Account settings', icon: User, onClick: () => { setOpen(false); navigate('/profile'); } },
    ];

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="top-[12vh] max-w-2xl translate-y-0 gap-0 overflow-hidden p-0 data-[state=open]:slide-in-from-top-2 [&>button:last-child]:hidden">
                <DialogTitle className="sr-only">Search</DialogTitle>
                <DialogDescription className="sr-only">Search for VINs, case IDs or plates across agencies.</DialogDescription>
                <div className="flex max-h-[70vh] flex-col">
                    <div className="flex items-center gap-3 border-b px-4">
                        {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <Search className="h-5 w-5 text-muted-foreground" />}
                        <input
                            autoFocus
                            placeholder="Search VIN, plate or case ID…"
                            className="h-14 flex-1 border-none bg-transparent text-body text-foreground outline-none placeholder:text-muted-foreground"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={onInputKeyDown}
                            aria-controls="search-results"
                            aria-activedescendant={results[activeIndex] ? `search-result-${results[activeIndex].id}` : undefined}
                        />
                        <Kbd>Esc</Kbd>
                    </div>

                    <div className="min-h-[18rem] flex-1 overflow-y-auto p-2" id="search-results" role="listbox">
                        {!query && (
                            <div className="space-y-2 p-2">
                                <p className="overline-label px-2">Jump to</p>
                                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                                    {quickLinks.map(link => (
                                        <button
                                            key={link.label}
                                            onClick={link.onClick}
                                            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-body-sm font-medium text-secondary-foreground transition-colors hover:bg-accent hover:text-foreground"
                                        >
                                            <link.icon className="h-4 w-4 text-muted-foreground" />
                                            {link.label}
                                        </button>
                                    ))}
                                </div>
                                <p className="px-2 pt-4 text-caption text-muted-foreground">
                                    Tip: type 5+ characters of a VIN to query the live registry.
                                </p>
                            </div>
                        )}

                        {query && !isLoading && results.length === 0 && (
                            <EmptyState icon={SearchX} title={`No results for "${query}"`} description="Try the full 17-character VIN or a case ID." size="sm" />
                        )}

                        {results.length > 0 && (
                            <div className="space-y-0.5">
                                <p className="overline-label px-3 py-2">{results.length} result{results.length !== 1 ? 's' : ''}</p>
                                {results.map((result, i) => (
                                    <button
                                        key={result.id}
                                        id={`search-result-${result.id}`}
                                        role="option"
                                        aria-selected={i === activeIndex}
                                        onMouseEnter={() => setActiveIndex(i)}
                                        onClick={() => handleSelect(result)}
                                        className={cn(
                                            'group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                                            i === activeIndex ? 'bg-accent' : 'hover:bg-accent'
                                        )}
                                    >
                                        <IconTile icon={RESULT_ICON[result.type] ?? Command} tone="brand" size="sm" />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate font-mono text-body-sm font-semibold uppercase text-foreground">{result.title}</p>
                                            <p className="truncate text-caption text-muted-foreground">{result.subtitle}</p>
                                        </div>
                                        <ArrowRight className={cn('h-4 w-4 text-muted-foreground transition-opacity', i === activeIndex ? 'opacity-100' : 'opacity-0')} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-4 border-t bg-subtle px-4 py-2.5 text-caption text-muted-foreground">
                        <span className="flex items-center gap-1.5"><Kbd>↑</Kbd><Kbd>↓</Kbd> Navigate</span>
                        <span className="flex items-center gap-1.5"><Kbd>↵</Kbd> Open</span>
                        <span className="flex items-center gap-1.5"><Kbd>Esc</Kbd> Close</span>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const RESULT_ICON: Record<SearchResult['type'], LucideIcon> = {
    vin: Car,
    case: FileText,
    user: User,
    license: Search,
};
